import { useEffect, useRef, useState } from 'react';
import { submitLead } from '../lib/api';
import { t, useLocale } from '../lib/i18n';

type FormState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'sent' }
  | { kind: 'error'; msg: string };

/**
 * Country dial-code list. Compact set: the markets we expect inquiries
 * from (BR + LATAM + Iberian + EN). Order chosen so BR sits first.
 * Flag emoji + dial code drive the visible chip; the emoji also reads
 * as a country mark on systems with native flag support (macOS, iOS,
 * modern Windows). Keep the list short — a 195-country `<select>`
 * would be a usability tax for a niche site that's 95 % BR audience.
 */
interface DialCountry {
  code: string;        // ISO-2
  name: string;
  dial: string;        // E.164 prefix without "+"
  flag: string;        // emoji flag (regional indicator pair)
}
const DIAL_COUNTRIES: DialCountry[] = [
  { code: 'BR', name: 'Brasil',          dial: '55',  flag: '🇧🇷' },
  { code: 'PT', name: 'Portugal',        dial: '351', flag: '🇵🇹' },
  { code: 'US', name: 'United States',   dial: '1',   flag: '🇺🇸' },
  { code: 'AR', name: 'Argentina',       dial: '54',  flag: '🇦🇷' },
  { code: 'UY', name: 'Uruguay',         dial: '598', flag: '🇺🇾' },
  { code: 'CL', name: 'Chile',           dial: '56',  flag: '🇨🇱' },
  { code: 'CO', name: 'Colombia',        dial: '57',  flag: '🇨🇴' },
  { code: 'MX', name: 'México',          dial: '52',  flag: '🇲🇽' },
  { code: 'ES', name: 'España',          dial: '34',  flag: '🇪🇸' },
  { code: 'GB', name: 'United Kingdom',  dial: '44',  flag: '🇬🇧' },
  { code: 'CA', name: 'Canada',          dial: '1',   flag: '🇨🇦' },
  { code: 'FR', name: 'France',          dial: '33',  flag: '🇫🇷' },
  { code: 'IT', name: 'Italia',          dial: '39',  flag: '🇮🇹' },
  { code: 'DE', name: 'Deutschland',     dial: '49',  flag: '🇩🇪' },
  { code: 'JP', name: 'Japan',           dial: '81',  flag: '🇯🇵' },
];

/**
 * Closing CTA / access request. Typed underlines instead of boxy
 * inputs — keeps the surface minimal and lets typography carry it.
 * Submission goes to `showcase_leads` as `pending`; the PM approves
 * from Builder before the prospect receives a tokenized link.
 */
export function LeadForm({ slug }: { slug: string }) {
  // Re-render whenever locale changes so t() picks up the new strings.
  // Wave 100 hardening: we read the locale value here ONCE and reuse it
  // throughout the JSX. The previous version called `useLocale()`
  // again *inside* a conditional JSX branch (the submitting button
  // label), which violates the Rules of Hooks and crashed the page
  // the moment the user pressed "Solicitar acesso" — the symptom JP
  // saw was an instant blank screen.
  const [locale] = useLocale();
  const [state, setState] = useState<FormState>({ kind: 'idle' });
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  // Phone is split into a country code (default BR) + the local
  // subscriber number, so we can prefix `+<dial>` cleanly on submit.
  const [dialCode, setDialCode] = useState<string>('55');
  const [phoneLocal, setPhoneLocal] = useState('');
  const [message, setMessage] = useState('');
  const sectionRef = useRef<HTMLElement>(null);

  const disabled = state.kind === 'submitting' || state.kind === 'sent';

  // Wave 75 · public-Housy bridge. When the chat detects purchase intent
  // ("quero ver", "quanto custa", "agendar visita") the widget dispatches
  // `housy:lead-intent` with the visitor's question. We pre-fill the
  // message field so they don't retype, then scroll the section into
  // view so they can see what's about to happen. Only mutate fields the
  // user hasn't already filled in — never overwrite their typing.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ question?: string }>).detail;
      if (detail?.question && !message.trim()) {
        setMessage(detail.question);
      }
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    window.addEventListener('housy:lead-intent', handler);
    return () => window.removeEventListener('housy:lead-intent', handler);
  }, [message]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim() || !email.trim()) {
      setState({ kind: 'error', msg: 'Nome e e-mail são obrigatórios.' });
      return;
    }
    setState({ kind: 'submitting' });
    try {
      // Compose the E.164-style phone string only when the local part
      // is non-empty; otherwise send null so we don't write a stub
      // "+55" to the lead record.
      const trimmedLocal = phoneLocal.trim();
      const phoneOut = trimmedLocal ? `+${dialCode} ${trimmedLocal}` : null;
      await submitLead(slug, {
        fullName: fullName.trim(),
        email: email.trim().toLowerCase(),
        phone: phoneOut,
        message: message.trim() || null,
      });
      setState({ kind: 'sent' });
    } catch (err) {
      setState({
        kind: 'error',
        msg: err instanceof Error ? err.message : 'Falha ao enviar.',
      });
    }
  };

  return (
    <section id="lead" ref={sectionRef} className="relative border-t border-line bg-bg py-24 sm:py-32">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-16 px-6 sm:px-12 lg:grid-cols-2 lg:px-20">
        <div>
          <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-accent">
            {t('lead.eyebrow')}
          </p>
          <h2 className="mt-5 max-w-[15ch] font-display text-4xl font-light leading-[1.05] tracking-tighter text-ink sm:text-5xl">
            {t('lead.title')}
          </h2>
          <p className="mt-6 max-w-prose text-sm leading-relaxed text-ink-muted sm:text-base">
            {t('lead.body')}
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-5">
          <Field label={t('lead.fullName')} required>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.currentTarget.value)}
              disabled={disabled}
              autoComplete="name"
              required
            />
          </Field>
          <Field label={t('lead.email')} required>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              disabled={disabled}
              autoComplete="email"
              required
            />
          </Field>
          <Field label={t('lead.phone')} optional>
            <div className="flex items-baseline gap-3">
              {/* Country selector — flag + dial code chip. Native
                  `<select>` so the phone keyboard / accessibility tree
                  on mobile stays first-class. The flag emoji renders
                  via the user's OS font (macOS, iOS, Win 11 native). */}
              <div className="relative inline-flex items-center gap-1.5">
                <span aria-hidden className="text-base leading-none">
                  {DIAL_COUNTRIES.find((c) => c.dial === dialCode)?.flag ?? '🌐'}
                </span>
                <span className="text-sm tabular-nums text-ink">
                  +{dialCode}
                </span>
                <select
                  aria-label="País"
                  value={dialCode}
                  onChange={(e) => setDialCode(e.currentTarget.value)}
                  disabled={disabled}
                  className="absolute inset-0 cursor-pointer opacity-0"
                >
                  {DIAL_COUNTRIES.map((c) => (
                    <option key={c.code} value={c.dial}>
                      {c.flag}  {c.name} (+{c.dial})
                    </option>
                  ))}
                </select>
              </div>
              <input
                type="tel"
                value={phoneLocal}
                onChange={(e) => setPhoneLocal(e.currentTarget.value)}
                disabled={disabled}
                autoComplete="tel-national"
                placeholder="11 9 9999 9999"
                className="flex-1"
              />
            </div>
          </Field>
          <Field label={t('lead.message')} optional>
            <textarea
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.currentTarget.value)}
              disabled={disabled}
              maxLength={500}
              placeholder="O que você gostaria de saber?"
            />
          </Field>

          {state.kind === 'error' && (
            <p className="text-sm text-red-400">{state.msg}</p>
          )}
          {state.kind === 'sent' ? (
            <p className="text-sm text-accent">
              {t('lead.sent')}
            </p>
          ) : (
            <button
              type="submit"
              disabled={disabled}
              className="pressable relative inline-flex h-12 items-center justify-center overflow-hidden rounded-full bg-accent px-8 text-[13px] font-medium uppercase tracking-[0.15em] text-bg transition-transform duration-150 ease-[cubic-bezier(0.32,0.72,0,1)] hover:scale-[1.02] active:scale-[0.97] focus:ring-2 focus:ring-accent/40 focus:ring-offset-2 focus:ring-offset-bg disabled:cursor-not-allowed disabled:opacity-60"
            >
              <span className="relative z-10">
                {state.kind === 'submitting'
                  ? locale === 'en' ? 'Sending…' : 'Enviando…'
                  : t('lead.submit')}
              </span>
            </button>
          )}
        </form>
      </div>
    </section>
  );
}

function Field({
  label,
  required,
  optional,
  children,
}: {
  label: string;
  required?: boolean;
  optional?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-2">
      <span className="flex items-baseline gap-2 text-[10px] font-medium uppercase tracking-[0.2em] text-ink-subtle">
        {label}
        {/* Wave 299 — "opcional" badge was ink-subtle/60 (~29% alpha →
            sub-3:1 contrast), swapped to ink-subtle (48%) so it stays
            quieter than the field label without dropping below WCAG
            AA legibility on the deep-black canvas. */}
        {optional && (
          <span className="text-[9px] normal-case tracking-normal text-ink-subtle">
            — {t('lead.optional')}
          </span>
        )}
        {required && (
          <span aria-hidden className="text-accent">
            *
          </span>
        )}
      </span>
      <div className="rounded-none border-b border-line pb-2 transition-colors duration-200 focus-within:border-accent">
        {children}
      </div>
    </label>
  );
}
